<template>
  <div>
    <base-header type>
      <div class="row align-items-center py-4">
        <breadcrumbs :header="$t('Profile')"></breadcrumbs>
      </div>
    </base-header>
    <div class="container-fluid" style="max-width: 700px; margin-left: 0">
      <tabs tabNavClasses="nav-wrapper"
          tabContentClass="card"
      >
      <tab-pane id="settings">
        <div slot="title">
          <i class="ni ni-settings"></i>
          {{ $t('profilePage.tabs.settings') }}
        </div>
        <v-wait :for="waiterKeyword">
          <template slot="waiting">
            <div class="container loader">
              <div class="row justify-content-md-center">
                <div class="col-md-auto">
                  <spinner></spinner>
                </div>
              </div>
            </div>
          </template>
          <validation-observer v-slot="{handleSubmit}" ref="formValidator">
          <form class="needs-validation" @submit.prevent="handleSubmit(formSettingsSubmit)">
            <div class="form-row">
              <div class="col-md-6 col-xs-12">
                <base-input
                  type="input"
                  :name="$t('Name')"
                  :label="$t('Your name')"
                  :placeholder="$t('Example, John Doe')"
                  v-model="profile.name"
                >
                </base-input>
              </div>
            </div>
            <div class="form-row">
              <div class="col-md-6">
                <base-input
                  :label="$t('Interface language')"
                  :name="$t('Language')"
                  labelClasses="required form-control-label"
                  rules="required"
                >
                  <el-select class="select-success" v-model="profile.ui_lang">
                    <el-option
                      v-for="option in languages"
                      class="select-success"
                      :key="option.value"
                      :value="option.value"
                      :label="option.title"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>

            <base-button
              :loading="updating"
              type="success"
              native-type="submit"
            >{{ $t('Save settings') }}
            </base-button>
            <base-button
              type="warning"
              @click="removeAccount"
            >{{ $t('Delete my account') }}
            </base-button>
          </form>
        </validation-observer>
        </v-wait>

      </tab-pane>
      <tab-pane id="author" v-if="isAuthor">
        <div slot="title"
             v-text="$t('profilePage.tabs.author')">
        </div>
        <validation-observer v-slot="{handleSubmit}" ref="formValidator">
          <form class="needs-validation" @submit.prevent="handleSubmit(formAuthorSubmit)">
            <div class="form-row">
              <div class="col-md-12">
                <base-input :label="$t('Your bio')" :name="$t('bio')">
                  <editor
                    tinymce-script-src="/static/src/js/tinymce/tinymce.min.js"
                    v-model="author.bio"></editor>
                </base-input>
              </div>
            </div>
            <div class="form-row">
              <div class="col-md-12">
                <meta-input v-model="author.meta"></meta-input>
              </div>
            </div>
            <base-button
              :loading="updating"
              type="success"
              native-type="submit"
            >{{ $t('Save info') }}
            </base-button>
          </form>
        </validation-observer>
      </tab-pane>
    </tabs>
    </div>
  </div>
</template>
<script>
  import Breadcrumbs from "../../Layout/Breadcrumbs.vue";
  import TabPane from '@/components/Tabs/Tab.vue';
  import Tabs from '@/components/Tabs/Tabs.vue';
  import Spinner from "vue-spinner-component/src/Spinner.vue";
  import {tryExceptAwait} from "../../../utils";
  import profileApi from "../../../api/profile";
  import {ProfileModel} from "../../../mixins/Profile/ProfileForm";
  import {AuthorModel} from "../../../mixins/Profile/AuthorForm";
  import MetaInput from "../../../components/Inputs/MetaInput";
  import Editor from '@tinymce/tinymce-vue';

  export default {
    name: "Profile",
    metaInfo() {
      return {
        title: this.$i18n.t("Profile edit"),
      };
    },
    data() {
      return {
        avatar: [],
        waiterKeyword: "profile-data-fetching",
        profile: {...ProfileModel},
        author: {...AuthorModel},
        languages: [
          {value: "", title: this.$i18n.t("Choose language")},
          {value: "en", title: "English"},
          {value: "uk", title: "Українська"},
          {value: "ru", title: "Русский"}
        ],
      }
    },
    components: {
      MetaInput,
      Breadcrumbs,
      Tabs,
      TabPane,
      Spinner,
      Editor
    },
    async created() {
      await this.fetchData();
    },
    computed: {
      isAuthor() {
        return this.$store.getters['auth/isAuthor']
      },
      updating() {
        return this.$store.getters['profile/isUpdating']
      }
    },
    watch: {
      $route: "fetchData",
    },
    methods: {
      async fetchData() {
        this.$wait.start(this.waiterKeyword);
        await this.$store.dispatch("profile/loadProfile");
        const {ui_lang, name} = this.$store.getters['profile/getProfile']
        this.profile = {...this.profile, ui_lang, name}
        if (this.isAuthor) {
          const {bio, meta} = this.$store.getters['profile/getProfile']
          this.author = {bio, meta: meta || []}
        }
        this.$wait.end(this.waiterKeyword);
      },
      async filesChange(files) {
        this.avatar = files;
        await this.updateAvatar()
      },
      async updateAvatar() {
        if (this.avatar.length > 0) {
          const form = new FormData();
          form.append("file", this.avatar[0]);
          const response = await tryExceptAwait(profileApi.uploadAvatar, [
            this.$route.params,
            form,
          ]);
          if (response.statusCode === 200 && response.data.msg === "Updated") {
            this.$notify({
              verticalAlign: 'top',
              type: 'success',
              horizontalAlign: 'center',
              message: this.$i18n.t("Avatar updated successfully")
            });
          } else {
            console.error(response.data);
            this.$notify({
              verticalAlign: 'top',
              type: 'error',
              horizontalAlign: 'center',
              message: this.$i18n.t("Update failure")
            });
          }
        }
      },
      async formSettingsSubmit() {
        await this.$store.dispatch(
          "profile/saveProfile",
          {
            urlParams: this.$route.params,
            form: {...this.profile},
          });
      },
      async formAuthorSubmit() {
        await this.$store.dispatch(
          "profile/saveProfile",
          {
            urlParams: this.$route.params,
            form: {...this.author},
          });
      },
      async removeAccount() {
        const answer = prompt("Вы хотите удалить свой аккаунт. " +
          "Это прям неожиданно, но все же мы не можем в этом отказать! " +
          "Введите свой email для подтверждения удаления своего аккаунта")
        if (answer === this.profile.email) {
          alert("Очень жаль! Ваш аккаунт будет удален в течении 30 дней!")
          await this.$router.push({name: "author-index"})
        }

      },
    },
  };
</script>
<style></style>
